import styled, { css } from 'styled-components'
import {
	orange,
	gold,
	danger,
	dangerTint,
	black,
	textMuted,
	mutedGrey,
	white,
	surface2,
	border2,
	scrimDark,
} from '@/theme/colors'
import { radii, motion, layers } from '@/theme/tokens'
import { fontSizes } from '@/theme/typography'
import { Button } from '@components/Button'
import { Input } from '@components/Field'

/** The shared form kit (Field + Button primitives) — one copy for every modal. */
export { Button, ButtonGroup, FormGroup, FormInput, FormLabel } from '@pages/Itinerary/AddEventModal/FormComponents.styles'

export const AmountInputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`

export const AmountPrefix = styled.span`
    position: absolute;
    left: 16px;
    font-size: ${fontSizes.body};
    color: ${textMuted};
    pointer-events: none;
`

export const AmountInput = styled(Input)`
	padding-left: 32px;
	-moz-appearance: textfield;

	&::-webkit-inner-spin-button,
	&::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
`



export const ParticipantsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 8px;
    padding-bottom: 8px;
`

export const ParticipantRow = styled.div<{ $isSelected: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: ${radii.small};
    cursor: pointer;
    transition: background ${motion.fast};
    background: ${({ $isSelected }) => ($isSelected ? `${gold}1f` : 'transparent')};

    &:hover {
        background: ${({ $isSelected }) => ($isSelected ? `${gold}1f` : surface2)};
    }
`

export const ParticipantCheckbox = styled.div<{ $checked: boolean }>`
    width: 20px;
    height: 20px;
    border-radius: ${radii.tiny};
    border: 2px solid ${({ $checked }) => ($checked ? orange : border2)};
    background: ${({ $checked }) => ($checked ? orange : 'white')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all ${motion.fast};

    &::after {
        content: '${({ $checked }) => ($checked ? '\\2713' : '')}';
        color: ${white};
        font-size: ${fontSizes.micro};
        font-weight: 700;
    }
`

export const ParticipantName = styled.span`
    font-size: ${fontSizes.small};
    font-weight: 500;
    color: ${black};
    flex: 1;
`

export const ParticipantSplitInput = styled(Input)`
	width: 80px;
	height: 32px;
	padding: 0 8px;
	font-size: ${fontSizes.caption};
	text-align: right;
	-moz-appearance: textfield;

	&::-webkit-inner-spin-button,
	&::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
`

export const SplitSummary = styled.div`
    font-size: ${fontSizes.micro};
    color: ${textMuted};
    margin-top: 8px;
    padding: 8px 12px;
    background: ${surface2};
    border-radius: ${radii.small};
`

export const SplitError = styled.span`
    color: ${danger};
    font-weight: 500;
`

export const FormDescription = styled.p`
    font-size: ${fontSizes.micro};
    color: ${mutedGrey};
    margin: -4px 0 8px;
`

export const ImageSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

export const ImagePreview = styled.div`
    width: 60px;
    height: 60px;
    border-radius: ${radii.small};
    overflow: hidden;
    cursor: pointer;
    flex-shrink: 0;
    position: relative;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

export const ImagePlaceholder = styled.label`
    width: 60px;
    height: 60px;
    border-radius: ${radii.small};
    border: 2px dashed ${border2};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: border-color ${motion.base}, background ${motion.base};
    background: ${surface2};

    &:hover {
        border-color: ${orange};
        background: ${gold}1f;
    }

    svg {
        width: 24px;
        height: 24px;
        color: ${mutedGrey};
    }
`

export const ImageActions = styled.div`
    display: flex;
    gap: 8px;
`

export const ImageActionButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })<{ $danger?: boolean }>`
	flex: 1;
	${({ $danger }) =>
		$danger &&
		css`
			color: ${danger};
			border-color: ${danger}40;

			&:hover:not(:disabled) {
				background: ${dangerTint};
				color: ${danger};
				border-color: ${danger};
			}
		`}
`

export const ImageOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: ${scrimDark};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${layers.viewer};
    cursor: pointer;

    img {
        max-width: 90vw;
        max-height: 90vh;
        object-fit: contain;
        border-radius: ${radii.small};
    }
`

